/**
 * `Toggle` — interruttore on/off riusabile del design system.
 *
 * È un checkbox nativo (`<input type="checkbox" role="switch">`) reso come uno
 * "switch" visivo: manteniamo la semantica e l'accessibilità del controllo
 * nativo (stato `checked`, supporto tastiera, form) e sostituiamo solo
 * l'aspetto tramite CSS (`.ui-toggle`).
 *
 * È controllato: `checked` e `onChange` vivono nel genitore. Fornire sempre
 * un'etichetta accessibile tramite `label` (visibile a fianco) oppure, quando
 * l'etichetta è altrove, `aria-label`/`aria-labelledby`.
 */
import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

interface ToggleProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange'> {
  /** Stato dell'interruttore. */
  checked: boolean;
  /** Notifica il nuovo stato quando l'utente commuta l'interruttore. */
  onChange: (next: boolean) => void;
  /** Etichetta visibile mostrata a fianco dell'interruttore. */
  label?: ReactNode;
  /** Posizione dell'etichetta rispetto allo switch. Default `end` (a destra). */
  labelPosition?: 'start' | 'end';
  /** Dimensione dello switch. `md` (default) o `sm` (compatta). */
  size?: 'sm' | 'md';
}

export function Toggle({
  checked,
  onChange,
  label,
  labelPosition = 'end',
  size = 'md',
  disabled = false,
  className,
  id,
  ...rest
}: ToggleProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const rootClasses = [
    'ui-toggle',
    size === 'sm' ? 'ui-toggle--sm' : '',
    labelPosition === 'start' ? 'ui-toggle--label-start' : '',
    disabled ? 'ui-toggle--disabled' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const control = (
    <span className="ui-toggle__control" aria-hidden="true">
      <span className="ui-toggle__track">
        <span className="ui-toggle__thumb" />
      </span>
    </span>
  );

  return (
    <label className={rootClasses} htmlFor={inputId}>
      {label != null && labelPosition === 'start' && (
        <span className="ui-toggle__label">{label}</span>
      )}
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        className="ui-toggle__input"
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        {...rest}
      />
      {control}
      {label != null && labelPosition === 'end' && (
        <span className="ui-toggle__label">{label}</span>
      )}
    </label>
  );
}

export default Toggle;
